import React from 'react';
import { Users, BookOpen, ClipboardList, GraduationCap, Award, FlaskConical } from 'lucide-react';
import { content, getText } from '../data/content';
import { combineClasses, getCardStyle } from '../theme/theme';
import Collapsible from '../components/Collapsible';

/**
 * ============================================================================
 * 页面：开放位置 / 招聘启事 - Opening.jsx
 * ============================================================================
 *
 * 【作用】单页展示招聘与加入信息：引导联系、当前/长期招聘、待遇与要求、科研方向、加入收获、
 *        科研会用到的课程、Mini Tests（若 content 中存在）、南科大 UROP/毕业设计/硕士、分组 Q&A。
 * 【如何修改】内容在 src/data/content-opening.js；各区块按对应键渲染。miniTests 区块仅在 content.openings.miniTests 存在时展示（当前已注释）。
 *
 * 【参数说明】props: lang, themeMode。
 */
const OpeningView = ({ lang, themeMode = "dark" }) => {
  const isLight = themeMode === "light";
  const hasMiniTests = !!content?.openings?.miniTests;

  return (
    <div className="min-h-screen pt-8 pb-20 px-4 max-w-4xl mx-auto animate-fade-in">
      {/* 页面标题 */}
      <div className="text-center mb-12">
        <h2 className={combineClasses(
          "text-4xl md:text-6xl font-bold mb-4",
          isLight ? "text-gray-900" : "text-white"
        )}>
          {getText('openings.title', lang)}
        </h2>
        <p className={combineClasses(
          "text-lg max-w-2xl mx-auto",
          isLight ? "text-gray-600" : "text-gray-400"
        )}>
          {getText('openings.subtitle', lang)}
        </p>
      </div>

      {/* 引导联系 */}
      <section className="mb-12">
        <div className={combineClasses(
          "p-6 rounded-xl border",
          getCardStyle(themeMode),
          "border-cyan-500/20"
        )}>
          <p className={combineClasses(
            "leading-relaxed whitespace-pre-line",
            isLight ? "text-gray-700" : "text-gray-300"
          )}>
            {getText('openings.intro', lang)}
          </p>
          <p className={combineClasses(
            "mt-3 text-sm whitespace-pre-line",
            isLight ? "text-gray-600" : "text-gray-400"
          )}>
            {getText('openings.contactPrompt', lang)}
          </p>
        </div>
      </section>

      {/* 当前招聘：开放位置页下拉列表，每岗位一点击展开/收起 */}
      {content?.openings?.currentOpenings?.positions && Object.keys(content.openings.currentOpenings.positions).length > 0 && (
        <section className="mb-12">
          <h3 className={combineClasses(
            "text-2xl font-bold mb-4 flex items-center gap-2",
            isLight ? "text-gray-900" : "text-white"
          )}>
            <ClipboardList className="w-6 h-6 text-cyan-400" />
            {getText('openings.currentOpenings.title', lang)}
          </h3>
          {/* 正常显示（无下拉）。需要下拉时：注释掉下面 div 块，取消注释再下面 Collapsible 块即可。 */}
          <div className="space-y-4">
            {Object.entries(content.openings.currentOpenings.positions).map(([key]) => (
              <div
                key={key}
                className={combineClasses("p-6 rounded-xl border", getCardStyle(themeMode))}
              >
                <h4 className={combineClasses("font-semibold mb-3", isLight ? "text-gray-900" : "text-white")}>
                  {getText(`openings.currentOpenings.positions.${key}.label`, lang)}
                </h4>
                <p className={combineClasses("leading-relaxed whitespace-pre-line text-sm", isLight ? "text-gray-700" : "text-gray-300")}>
                  {getText(`openings.currentOpenings.positions.${key}.body`, lang)}
                </p>
              </div>
            ))}
          </div>
          {/*
          <div className="space-y-3">
            {Object.entries(content.openings.currentOpenings.positions).map(([key]) => (
              <Collapsible
                key={key}
                label={getText(`openings.currentOpenings.positions.${key}.label`, lang)}
                isLight={isLight}
                defaultOpen={false}
                className={getCardStyle(themeMode)}
              >
                <p className={combineClasses("leading-relaxed whitespace-pre-line text-sm", isLight ? "text-gray-700" : "text-gray-300")}>
                  {getText(`openings.currentOpenings.positions.${key}.body`, lang)}
                </p>
              </Collapsible>
            ))}
          </div>
          */}
        </section>
      )}

      {/* 长期机会 */}
      <section className="mb-12">
        <h3 className={combineClasses(
          "text-2xl font-bold mb-4 flex items-center gap-2",
          isLight ? "text-gray-900" : "text-white"
        )}>
          <Users className="w-6 h-6 text-cyan-400" />
          {getText('openings.longTermOpenings.title', lang)}
        </h3>
        <div className={combineClasses(
          "p-6 rounded-xl border",
          getCardStyle(themeMode)
        )}>
          <p className={combineClasses(
            "leading-relaxed whitespace-pre-line",
            isLight ? "text-gray-700" : "text-gray-300"
          )}>
            {getText('openings.longTermOpenings.body', lang)}
          </p>
        </div>
      </section>

      {/* 待遇与要求：开放位置页下拉列表，每类别一点击展开/收起 */}
      {content?.openings?.benefitsAndRequirements?.byCategory && Object.keys(content.openings.benefitsAndRequirements.byCategory).length > 0 && (
        <section className="mb-12">
          <h3 className={combineClasses(
            "text-2xl font-bold mb-4 flex items-center gap-2",
            isLight ? "text-gray-900" : "text-white"
          )}>
            <Award className="w-6 h-6 text-cyan-400" />
            {getText('openings.benefitsAndRequirements.title', lang)}
          </h3>
          <div className="space-y-3">
            {Object.entries(content.openings.benefitsAndRequirements.byCategory).map(([key]) => (
              <Collapsible
                key={key}
                label={getText(`openings.benefitsAndRequirements.byCategory.${key}.label`, lang)}
                isLight={isLight}
                defaultOpen={false}
                className={getCardStyle(themeMode)}
              >
                <div className="space-y-4 text-sm">
                  <div>
                    <span className={combineClasses("font-medium block mb-1", isLight ? "text-gray-700" : "text-gray-300")}>
                      {lang === 'cn' ? '薪资/待遇说明' : 'Salary / compensation'}
                    </span>
                    <p className={combineClasses("leading-relaxed whitespace-pre-line", isLight ? "text-gray-600" : "text-gray-400")}>
                      {getText(`openings.benefitsAndRequirements.byCategory.${key}.salary`, lang)}
                    </p>
                  </div>
                  <div>
                    <span className={combineClasses("font-medium block mb-1", isLight ? "text-gray-700" : "text-gray-300")}>
                      {lang === 'cn' ? '福利' : 'Benefits'}
                    </span>
                    <p className={combineClasses("leading-relaxed whitespace-pre-line", isLight ? "text-gray-600" : "text-gray-400")}>
                      {getText(`openings.benefitsAndRequirements.byCategory.${key}.benefits`, lang)}
                    </p>
                  </div>
                  <div>
                    <span className={combineClasses("font-medium block mb-1", isLight ? "text-gray-700" : "text-gray-300")}>
                      {lang === 'cn' ? '要求' : 'Requirements'}
                    </span>
                    <p className={combineClasses("leading-relaxed whitespace-pre-line", isLight ? "text-gray-600" : "text-gray-400")}>
                      {getText(`openings.benefitsAndRequirements.byCategory.${key}.requirements`, lang)}
                    </p>
                  </div>
                </div>
              </Collapsible>
            ))}
          </div>
        </section>
      )}

      {/* 科研方向与要求：开放位置页下拉，整块可展开/收起 */}
      {getText('openings.researchDirections.title', lang) && (
        <section className="mb-12">
          <h3 className={combineClasses(
            "text-2xl font-bold mb-4 flex items-center gap-2",
            isLight ? "text-gray-900" : "text-white"
          )}>
            <FlaskConical className="w-6 h-6 text-cyan-400" />
            {getText('openings.researchDirections.title', lang)}
          </h3>
          {/* 正常显示（无下拉）。需要下拉时：注释掉下面 div 块，取消注释再下面的 Collapsible 块即可。 */}
          <div className={combineClasses("p-6 rounded-xl border", getCardStyle(themeMode))}>
            <p className={combineClasses("leading-relaxed whitespace-pre-line mb-4", isLight ? "text-gray-700" : "text-gray-300")}>
              {getText('openings.researchDirections.intro', lang)}
            </p>
            <ul className="space-y-3">
              {(getText('openings.researchDirections.pillars', lang) || []).map((pillar, i) => (
                <li key={i}>
                  <span className={combineClasses("font-semibold block mb-1", isLight ? "text-gray-900" : "text-white")}>
                    {typeof pillar?.name === 'string' ? pillar.name : pillar?.name?.[lang] ?? pillar?.name?.en ?? ''}
                  </span>
                  <p className={combineClasses("text-sm leading-relaxed pl-0 whitespace-pre-line", isLight ? "text-gray-600" : "text-gray-400")}>
                    {typeof pillar?.desc === 'string' ? pillar.desc : pillar?.desc?.[lang] ?? pillar?.desc?.en ?? ''}
                  </p>
                </li>
              ))}
            </ul>
          </div>
          {/*
          <Collapsible
            label={lang === 'cn' ? '说明与各环节' : 'Overview & pillars'}
            isLight={isLight}
            defaultOpen={false}
            className={getCardStyle(themeMode)}
          >
            <p className={combineClasses("leading-relaxed whitespace-pre-line mb-4", isLight ? "text-gray-700" : "text-gray-300")}>
              {getText('openings.researchDirections.intro', lang)}
            </p>
            <ul className="space-y-3">
              {(getText('openings.researchDirections.pillars', lang) || []).map((pillar, i) => (
                <li key={i}>
                  <span className={combineClasses("font-semibold block mb-1", isLight ? "text-gray-900" : "text-white")}>
                    {typeof pillar?.name === 'string' ? pillar.name : pillar?.name?.[lang] ?? pillar?.name?.en ?? ''}
                  </span>
                  <p className={combineClasses("text-sm leading-relaxed pl-0 whitespace-pre-line", isLight ? "text-gray-600" : "text-gray-400")}>
                    {typeof pillar?.desc === 'string' ? pillar.desc : pillar?.desc?.[lang] ?? pillar?.desc?.en ?? ''}
                  </p>
                </li>
              ))}
            </ul>
          </Collapsible>
          */}
        </section>
      )}

      {/* 加入后收获 */}
      <section className="mb-12">
        <h3 className={combineClasses(
          "text-2xl font-bold mb-4 flex items-center gap-2",
          isLight ? "text-gray-900" : "text-white"
        )}>
          <GraduationCap className="w-6 h-6 text-cyan-400" />
          {getText('openings.whatYouGain.title', lang)}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(getText('openings.whatYouGain.items', lang) || []).map((item, i) => (
            <div
              key={i}
              className={combineClasses(
                "p-5 rounded-xl border",
                getCardStyle(themeMode),
                "hover:border-cyan-500/30 transition-colors"
              )}
            >
              <h4 className={combineClasses(
                "font-semibold mb-2",
                isLight ? "text-gray-900" : "text-white"
              )}>
                {typeof item?.title === 'string' ? item.title : item?.title?.[lang] ?? item?.title?.en ?? ''}
              </h4>
              <p className={combineClasses(
                "text-sm leading-relaxed",
                isLight ? "text-gray-600" : "text-gray-400"
              )}>
                {typeof item?.desc === 'string' ? item.desc : item?.desc?.[lang] ?? item?.desc?.en ?? ''}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* 
      {/* 科研会用到的课程与技能（非建议先修）
      <section className="mb-12">
        <h3 className={combineClasses(
          "text-2xl font-bold mb-4 flex items-center gap-2",
          isLight ? "text-gray-900" : "text-white"
        )}>
          <BookOpen className="w-6 h-6 text-cyan-400" />
          {getText('openings.coursesUsedInResearch.title', lang)}
        </h3>
        <div className={combineClasses(
          "p-6 rounded-xl border",
          getCardStyle(themeMode)
        )}>
          <p className={combineClasses(
            "mb-6 leading-relaxed whitespace-pre-line",
            isLight ? "text-gray-700" : "text-gray-300"
          )}>
            {getText('openings.coursesUsedInResearch.intro', lang)}
          </p>
          <ul className="space-y-4">
            {(getText('openings.coursesUsedInResearch.categories', lang) || []).map((cat, i) => (
              <li key={i}>
                <span className={combineClasses(
                  "font-semibold block mb-1",
                  isLight ? "text-gray-900" : "text-white"
                )}>
                  {typeof cat?.name === 'string' ? cat.name : cat?.name?.[lang] ?? cat?.name?.en ?? ''}
                </span>
                <p className={combineClasses(
                  "text-sm leading-relaxed pl-0 whitespace-pre-line",
                  isLight ? "text-gray-600" : "text-gray-400"
                )}>
                  {typeof cat?.items === 'string' ? cat.items : cat?.items?.[lang] ?? cat?.items?.en ?? ''}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </section>  */}

      {/* Mini Tests（可选）：开放位置页下拉，每组一点击展开/收起 */}
      {hasMiniTests && (() => {
        const mt = content.openings.miniTests;
        const groupKeys = Object.keys(mt).filter((k) => k.startsWith('for') && mt[k]?.label && Array.isArray(mt[k].items));
        if (groupKeys.length === 0) return null;
        return (
          <section className="mb-12">
            <h3 className={combineClasses(
              "text-2xl font-bold mb-2 flex items-center gap-2",
              isLight ? "text-gray-900" : "text-white"
            )}>
              <ClipboardList className="w-6 h-6 text-cyan-400" />
              {getText('openings.miniTests.title', lang)}
            </h3>
            <p className={combineClasses("mb-4 text-sm leading-relaxed", isLight ? "text-gray-600" : "text-gray-400")}>
              {getText('openings.miniTests.subtitle', lang)}
            </p>
            <div className="space-y-3">
              {groupKeys.map((key) => (
                <Collapsible
                  key={key}
                  label={getText(`openings.miniTests.${key}.label`, lang)}
                  isLight={isLight}
                  defaultOpen={false}
                  className={getCardStyle(themeMode)}
                >
                  <ul className="space-y-2 list-disc list-inside text-sm">
                    {(getText(`openings.miniTests.${key}.items`, lang) || []).map((item, i) => (
                      <li key={i} className={combineClasses("leading-relaxed", isLight ? "text-gray-600" : "text-gray-400")}>
                        {typeof item === 'string' ? item : item?.[lang] ?? item?.en ?? ''}
                      </li>
                    ))}
                  </ul>
                </Collapsible>
              ))}
            </div>
          </section>
        );
      })()}

      {/* 南科大本科毕业设计 / 硕士研究 
      <section className="mb-12">
        <h3 className={combineClasses(
          "text-2xl font-bold mb-4 flex items-center gap-2",
          isLight ? "text-gray-900" : "text-white"
        )}>
          <GraduationCap className="w-6 h-6 text-cyan-400" />
          {getText('openings.sustechCredits.title', lang)}
        </h3>
        <div className={combineClasses(
          "p-6 rounded-xl border space-y-6",
          getCardStyle(themeMode)
        )}>
          <p className={combineClasses(
            "leading-relaxed whitespace-pre-line",
            isLight ? "text-gray-700" : "text-gray-300"
          )}>
            {getText('openings.sustechCredits.overview', lang)}
          </p>
          <div>
            <h4 className={combineClasses(
              "font-semibold mb-2",
              isLight ? "text-gray-900" : "text-white"
            )}>
              {getText('openings.sustechCredits.fyp.title', lang)}
            </h4>
            <p className={combineClasses(
              "text-sm leading-relaxed whitespace-pre-line",
              isLight ? "text-gray-600" : "text-gray-400"
            )}>
              {getText('openings.sustechCredits.fyp.body', lang)}
            </p>
          </div>
          <div>
            <h4 className={combineClasses(
              "font-semibold mb-2",
              isLight ? "text-gray-900" : "text-white"
            )}>
              {getText('openings.sustechCredits.urop.title', lang)}
            </h4>
            <p className={combineClasses(
              "text-sm leading-relaxed whitespace-pre-line",
              isLight ? "text-gray-600" : "text-gray-400"
            )}>
              {getText('openings.sustechCredits.urop.body', lang)}
            </p>
          </div>
          <div>
            <h4 className={combineClasses(
              "font-semibold mb-2",
              isLight ? "text-gray-900" : "text-white"
            )}>
              {getText('openings.sustechCredits.master.title', lang)}
            </h4>
            <p className={combineClasses(
              "text-sm leading-relaxed whitespace-pre-line",
              isLight ? "text-gray-600" : "text-gray-400"
            )}>
              {getText('openings.sustechCredits.master.body', lang)}
            </p>
          </div>
        </div>
      </section> */}

      {/* 分组常见问题：开放位置页下拉，每组（招生/需要学的内容等）一点击展开/收起 */}
      {(getText('openings.faqSections', lang) || []).length > 0 && (
        <section className="mb-12">
          <h3 className={combineClasses(
            "text-2xl font-bold mb-4 flex items-center gap-2",
            isLight ? "text-gray-900" : "text-white"
          )}>
            <ClipboardList className="w-6 h-6 text-cyan-400" />
            {lang === 'cn' ? '常见问题' : 'Q & A'}
          </h3>
          <div className="space-y-3">
            {(getText('openings.faqSections', lang) || []).map((section, si) => (
              <Collapsible
                key={si}
                label={typeof section?.sectionTitle === 'string' ? section.sectionTitle : section?.sectionTitle?.[lang] ?? section?.sectionTitle?.en ?? ''}
                isLight={isLight}
                defaultOpen={false}
                className={getCardStyle(themeMode)}
              >
                <ul className="space-y-3">
                  {(section?.items || []).map((qa, qi) => (
                    <li key={qi} className="text-sm">
                      <span className={combineClasses("font-medium block mb-1", isLight ? "text-gray-800" : "text-gray-200")}>
                        {typeof qa?.q === 'string' ? qa.q : qa?.q?.[lang] ?? qa?.q?.en ?? ''}
                      </span>
                      <span className={combineClasses("leading-relaxed whitespace-pre-line block", isLight ? "text-gray-600" : "text-gray-400")}>
                        {typeof qa?.a === 'string' ? qa.a : qa?.a?.[lang] ?? qa?.a?.en ?? ''}
                      </span>
                    </li>
                  ))}
                </ul>
              </Collapsible>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default OpeningView;
